'use client';

import JournalOverview from "@/src/components/journal-site/home/journal-overview/journal-overview";
import JournalMetrics from "@/src/components/journal-site/home/journal-metrics/journal-metrics";
import JournalKeywords from "@/src/components/journal-site/home/journal-keywords/journal-keywords";
import {Box} from "@mui/material";
import Journal from "@/src/_types/journal";
import JournalSocial from "@/src/components/journal-site/common/journal-social/journal-social";
import CardWidget from "@/src/components/journal-site/common/card-widget/card-widget";
import ArticleList from "@/src/_types/Article/article-list";
import InPress from "@/src/_types/in-press";
import lastArticles from "@/src/_types/last-article";
import InPressArticles from "@/src/components/journal-site/home/in-press-articles/in-press-articles";
import {IN_PRESS_ARTICLES, RECENT_ARTICLES_TITLE} from "@/src/constant/constant";
import Index from "@/src/components/journal-site/home/index";
import Articles from "@/src/components/journal-site/common/articles/articles";
import {MapToDocumentListItems} from "@/src/utils/database-helpers/mapper/document-mapper";
import Issue from "@/src/_types/issue";
import Page from "@/src/_types/Page/page";

function fetchInPressDocuments(documents: any[], journal: Journal): InPress {
    const inPressDocuments: ArticleList[] = MapToDocumentListItems({journal, documents});
    inPressDocuments.sort((a, b) => {
        return a.display_order - b.display_order;
    });

    return {
        articles: inPressDocuments,
    };
}

function fetchLastDocuments(documents: any[], journal: Journal, lastIssue: Issue): lastArticles {
    const lastDocuments: ArticleList[] = MapToDocumentListItems({journal, documents});
    lastDocuments.sort((a, b) => {
        return a.display_order - b.display_order;
    });

    return {
        articles: lastDocuments,
        caption: lastIssue?.caption,
        volume: lastIssue?.volume_name ?? '',
        date: lastIssue?.formatted_publish_date,
        xmlLink: `${journal.journal_url}/issue/${lastIssue?.id}.xml`,
    };
}

export default function JournalSiteHome({
                                            journal,
                                            lastIssue,
                                            lastDocumentsData,
                                            inPressDocumentsData,
                                            widgets,
                                            welcomeMsg,
                                        }: {
    journal: any;
    lastIssue: any;
    lastDocumentsData: any;
    inPressDocumentsData: any;
    widgets: Page[];
    welcomeMsg: any;
}) {
    const inPressDocuments = fetchInPressDocuments(inPressDocumentsData, journal);
    const lastDocuments = fetchLastDocuments(lastDocumentsData, journal, lastIssue);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: {lg: 'row', xs: 'column'},
                paddingY: 2,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: .5,
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    flex: {lg: 9, xs: 12},
                    width: {lg: '75%', xs: '100%'},
                }}
            >
                <JournalOverview journal={journal}/>
                {(inPressDocuments.articles?.length ?? 0) > 0 && (
                    <InPressArticles
                        title={IN_PRESS_ARTICLES}
                        articles={inPressDocuments.articles}
                    />
                )}

                {(lastDocuments.articles?.length ?? 0) === 0 ? (
                    <Index journal={journal} welcomeMsg={welcomeMsg}/>
                ) : (
                    <Articles
                        title={RECENT_ARTICLES_TITLE}
                        articles={lastDocuments.articles}
                        date={lastDocuments.date}
                        volume={lastDocuments.volume}
                        caption={lastDocuments.caption}
                        desc={journal.options?.template_configs?.journal_recent_article?.recent_article?.caption}
                    />
                )}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: .5,
                    alignItems: {lg: 'flex-start', xs: 'center'},
                    justifyContent: 'flex-start',
                    flex: {lg: 3, xs: 12},
                    width: {lg: '25%', xs: '100%'},
                    marginLeft: {lg: .5, xs: 0},
                    marginTop: {lg: 0, xs: .5},
                }}
            >
                <JournalMetrics journal={journal}/>
                <JournalKeywords journal={journal}/>
                <JournalSocial journal={journal}/>
                {widgets.map((value, index) => {
                    return (
                        <CardWidget value={value} key={index}/>
                    )
                })}
            </Box>
        </Box>
    )
}
